import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff, Clock, LogIn, LogOut, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { WaButtons } from "@/components/WaButtons";

const MODES = [["auto", "Otomatis"], ["masuk", "Masuk"], ["pulang", "Pulang"]];
const witNow = () => new Date().toLocaleTimeString("en-GB", { timeZone: "Asia/Jayapura", hour12: false });

const CameraScanner = ({ onCode }) => {
  const [on, setOn] = useState(false);
  const ref = useRef(null);
  const last = useRef({ code: "", at: 0 });
  useEffect(() => {
    if (!on) return undefined;
    const qr = new Html5Qrcode("qr-reader");
    ref.current = qr;
    qr.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 }, (code) => {
      const t = Date.now();
      if (code === last.current.code && t - last.current.at < 4000) return;
      last.current = { code, at: t };
      onCode(code);
    }, () => {}).catch(() => { toast.error("Kamera tidak dapat diakses. Periksa izin kamera browser."); setOn(false); });
    return () => { qr.isScanning ? qr.stop().then(() => qr.clear()).catch(() => {}) : qr.clear(); };
  }, [on]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="scan-camera">
      <div id="qr-reader" className={on ? "qr-box" : "qr-box idle"} data-testid="qr-reader">{!on && <div className="qr-idle"><Camera size={30} /><span>Kamera nonaktif</span></div>}</div>
      <button className={on ? "outline-btn full" : "primary-btn full"} onClick={() => setOn(!on)} data-testid="camera-toggle-button">
        {on ? <><CameraOff size={16} /> Matikan kamera</> : <><Camera size={16} /> Aktifkan kamera</>}
      </button>
    </div>
  );
};

const LastResult = ({ r, onSent }) => !r ? <div className="scan-result empty-result" data-testid="scan-result-empty">Belum ada scan. Arahkan kartu ke kamera atau gunakan scanner USB.</div> : r.error ? (
  <div className="scan-result bad" data-testid="scan-result-error"><b>Scan ditolak</b><span>{r.error}</span></div>
) : (
  <div className={`scan-result ${r.kind === "Positif" ? "good" : "warn"}`} data-testid="scan-result">
    <small>{r.type === "masuk" ? "SCAN MASUK" : "SCAN PULANG"} · {r.time} WIT</small>
    <b data-testid="scan-result-name">{r.student_name}</b><span>{r.class_name} · NIS {r.nis}</span>
    <strong data-testid="scan-result-category">{r.category}</strong>
    <WaButtons key={r.record_id} r={r} recordId={r.record_id} prefix="result-wa" onSent={onSent} />
  </div>
);

const ScanTable = ({ type, title, Icon, date, items, onSent }) => (
  <section className="panel table-panel" data-testid={`scan-table-${type}`}>
    <div className="panel-head"><div><h3><Icon size={16} /> {title}</h3><p data-testid={`scan-table-${type}-count`}>{items.length} siswa · {date}</p></div></div>
    <table><thead><tr><th>Waktu</th><th>Siswa</th><th>Catatan</th><th>Kirim pesan</th></tr></thead>
      <tbody>{items.map((a) => (
        <tr key={a.id} data-testid={`scan-row-${a.id}`}><td>{a.time}</td><td><b>{a.student_name}</b><small className="table-sub">{a.class_name} · {a.nis}</small></td>
          <td><span className={`status ${a.kind === "Positif" ? "verified" : "waiting"}`}>{a.category}</span></td><td><WaButtons r={a} recordId={a.record_id} onSent={onSent} /></td></tr>))}
      </tbody></table>
    {!items.length && <div className="empty">Belum ada scan {type} hari ini.</div>}
  </section>
);

export const ScanPage = () => {
  const [mode, setMode] = useState("auto");
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [today, setToday] = useState(null);
  const [clock, setClock] = useState(witNow());
  const input = useRef(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const load = () => api("/attendance/today").then(setToday).catch((e) => toast.error(e.message));
  useEffect(() => { load(); const t = setInterval(() => setClock(witNow()), 1000); return () => clearInterval(t); }, []);
  const submit = async (value) => {
    if (!value.trim()) return;
    try {
      const r = await api("/attendance/scan", { method: "POST", body: JSON.stringify({ code: value, mode: modeRef.current }) });
      setResult(r); toast.success(`${r.student_name}: ${r.category}`); load();
    } catch (e) { setResult({ error: e.message }); toast.error(e.message); }
    setCode(""); input.current?.focus();
  };
  const s = today?.summary || {};
  return (
    <div data-testid="scan-page">
      <div className="page-heading compact">
        <div><p className="eyebrow">KEHADIRAN HARIAN</p><h1>Scan kartu siswa</h1>
          <p className="muted">Masuk sebelum {today?.arrival_limit || "07:26"} WIT = tepat waktu · Pulang mulai {today?.departure_from || "13:00"} WIT = sesuai jadwal</p></div>
        <div className="wit-clock" data-testid="wit-clock"><Clock size={18} /><b>{clock}</b><span>WIT</span></div>
      </div>
      <div className="scan-grid">
        <section className="panel scan-panel">
          <div className="filter-pills" data-testid="scan-mode-group">{MODES.map(([v, l]) => <button key={v} className={mode === v ? "filter active" : "filter"} onClick={() => setMode(v)} data-testid={`scan-mode-${v}`}>{l}</button>)}</div>
          <p className="muted small-note">{mode === "auto" ? "Scan pertama hari ini dicatat masuk, scan berikutnya dicatat pulang." : `Semua scan dicatat sebagai ${mode}.`}</p>
          <CameraScanner onCode={submit} />
          <form onSubmit={(e) => { e.preventDefault(); submit(code); }} className="usb-form">
            <label>Scanner USB / ketik NIS<div className="search"><ScanLine size={17} /><input ref={input} autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="Arahkan scanner USB ke kartu…" data-testid="scan-code-input" /></div></label>
            <button className="primary-btn" data-testid="scan-submit-button">Catat</button>
          </form>
        </section>
        <section className="scan-side">
          <LastResult r={result} onSent={load} />
          <div className="stats-grid scan-stats">
            {[["Tepat waktu", s.on_time, "green", LogIn], ["Terlambat", s.late, "amber", LogIn], ["Pulang sesuai", s.left_ok, "blue", LogOut], ["Pulang awal", s.left_early, "rose", LogOut]].map(([l, v, c, Icon]) => (
              <div className="stat-card" key={l} data-testid={`scan-stat-${c}`}><div className={`stat-icon ${c}`}><Icon size={17} /></div><div><span>{l}</span><strong>{v ?? 0}</strong></div></div>))}
          </div>
        </section>
      </div>
      <div className="scan-tables">{[["masuk", "Scan masuk", LogIn], ["pulang", "Scan pulang", LogOut]].map(([t, title, Icon]) => <ScanTable key={t} type={t} title={title} Icon={Icon} date={today?.date} items={(today?.items || []).filter((a) => a.type === t)} onSent={load} />)}</div>
    </div>
  );
};
